import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

function useReveal(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); obs.unobserve(el); }
    }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

export default function NotreVision() {
  const [heroRef, heroVis] = useReveal(0.1);
  const [s1Ref, s1Vis] = useReveal(0.15);
  const [s1TxtRef, s1TxtVis] = useReveal(0.2);
  const [s2Ref, s2Vis] = useReveal(0.15);
  const [s2TxtRef, s2TxtVis] = useReveal(0.2);
  const [stepRef, stepVis] = useReveal(0.1);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div>
      {/* ── HERO ── */}
      <section className="relative h-[60vh] md:h-[70vh] flex items-center justify-center overflow-hidden bg-black">
        <img src="/photos/Sylvie-SMP_10.jpg" alt="Création artisanale"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: 'brightness(0.3) contrast(1.1) saturate(0.8)' }} />
        <div className="absolute inset-0" style={{ boxShadow: 'inset 0 0 200px 80px rgba(0,0,0,0.4)' }} />

        <div ref={heroRef} className={`relative z-10 text-center px-6 transition-all duration-[1.5s] ${heroVis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="w-10 h-px bg-white/20" />
            <div className="w-1.5 h-1.5 rotate-45 bg-white/40" />
            <div className="w-10 h-px bg-white/20" />
          </div>
          <p className="text-xs tracking-[0.5em] uppercase mb-6 font-light text-white/50">Le Savoir-Faire</p>
          <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl text-white font-light tracking-wider mb-6">
            Notre Vision
          </h1>
          <p className="text-white/35 text-sm md:text-base max-w-lg mx-auto leading-relaxed">
            De la pierre brute au bijou fini, un voyage artisanal.
          </p>
        </div>
      </section>

      {/* ── LA FABRICATION — Section 1 ── */}
      <section className="relative py-0 bg-white overflow-hidden">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-12 min-h-[80vh]">
          <div ref={s1Ref}
            className={`md:col-span-6 relative overflow-hidden transition-all duration-[1.5s] ${s1Vis ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
            <img src="/photos/Sylvie-SMP_3.jpg" alt="Sélection des pierres"
              className="w-full h-[500px] md:h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-white/10" />
          </div>

          <div ref={s1TxtRef}
            className={`md:col-span-6 flex items-center px-8 md:px-16 lg:px-20 py-20 md:py-0 transition-all duration-[1.5s] delay-300 ${s1TxtVis ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
            <div>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-8 h-px bg-gray-900" />
                <p className="text-xs tracking-[0.4em] uppercase font-medium text-gray-400">La Fabrication</p>
              </div>

              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-gray-900 mb-10 leading-tight">
                Tout commence<br />par une pierre
              </h2>

              <div className="space-y-6 mb-12">
                <p className="text-gray-500 leading-[1.9] text-[15px]">
                  Chez Pernety, tout commence par une pierre. Sur les marchés et les ateliers d'Asie
                  du Sud-Est, et surtout à Bangkok, hub mondial des pierres de couleur, haut lieu du
                  savoir-faire lapidaire et joaillier depuis plusieurs générations.
                </p>
                <p className="text-gray-500 leading-[1.9] text-[15px]">
                  Entre mon atelier parisien où je laisse courir mon imagination, et celui de mon artisan
                  joaillière partenaire en Thaïlande, le bijou naît autour de la pierre qui l'inspire.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── LE PROCESSUS — 3 ETAPES ── */}
      <section ref={stepRef} className="py-24 md:py-32 px-6 bg-stone-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-20">
            <p className="text-xs tracking-[0.5em] uppercase font-light text-gray-400 mb-4">Le Processus</p>
            <h2 className="font-serif text-3xl md:text-4xl text-gray-900">De la pierre au bijou</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8">
            {[
              {
                step: '01',
                title: 'La Pierre',
                desc: 'Sélection minutieuse des pierres de couleur. Chaque gemme est choisie pour son caractère unique, son éclat, sa lumière particulière.',
                img: '/photos/Sylvie-SMP_12.jpg',
              },
              {
                step: '02',
                title: 'Le Dessin',
                desc: 'Le bijou prend forme sur le papier. Inspiré par la pierre, le design naît de l\'émotion qu\'elle éveille, guidé par l\'intuition et l\'expérience.',
                img: '/photos/Sylvie-SMP_5.jpg',
              },
              {
                step: '03',
                title: 'La Création',
                desc: 'L\'artisanat prend le relais. Patience, précision et passion pour donner vie à une pièce unique, pensée pour être portée et transmise.',
                img: '/photos/Sylvie-SMP_2.jpg',
              },
            ].map((item, i) => (
              <div key={i}
                className={`transition-all duration-1000 ${stepVis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
                style={{ transitionDelay: `${i * 200}ms` }}>
                <div className="aspect-[4/5] overflow-hidden mb-6 group">
                  <img src={item.img} alt={item.title}
                    className="w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-105"
                    loading="lazy" />
                </div>
                <div className="text-center">
                  <p className="text-xs font-light text-gray-300 mb-2">{item.step}</p>
                  <h3 className="font-serif text-xl text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Section 2 — Atelier inversé ── */}
      <section className="relative py-0 bg-black overflow-hidden">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-12 min-h-[80vh]">
          <div ref={s2TxtRef}
            className={`md:col-span-6 flex items-center px-8 md:px-16 lg:px-20 py-20 md:py-0 order-2 md:order-1 transition-all duration-[1.5s] delay-300 ${s2TxtVis ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
            <div>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-8 h-px bg-white/30" />
                <p className="text-xs tracking-[0.4em] uppercase font-medium text-white/40">L'Atelier</p>
              </div>

              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white mb-10 leading-tight">
                Deux ateliers,<br />une même passion
              </h2>

              <div className="space-y-6">
                <p className="text-white/40 leading-[1.9] text-[15px]">
                  L'atelier parisien est le lieu de l'imagination, où les pierres rencontrent les idées.
                  C'est ici que naissent les dessins, que les collections prennent forme.
                </p>
                <p className="text-white/40 leading-[1.9] text-[15px]">
                  En Thaïlande, nos artisans joailliers partenaires donnent vie aux créations
                  avec un savoir-faire transmis depuis plusieurs générations.
                  Le bijou naît de cette rencontre entre deux mondes.
                </p>
              </div>
            </div>
          </div>

          <div ref={s2Ref}
            className={`md:col-span-6 relative overflow-hidden order-1 md:order-2 transition-all duration-[1.5s] ${s2Vis ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
            <img src="/photos/Sylvie-SMP_8.jpg" alt="L'atelier"
              className="w-full h-[500px] md:h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-l from-transparent to-black/30" />
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-gray-900 mb-6">
            Découvrir notre engagement
          </h2>
          <p className="text-gray-400 text-base leading-relaxed mb-10">
            La transparence est au cœur de notre démarche. Découvrez comment nous garantissons
            l'authenticité et la traçabilité de chaque pierre.
          </p>
          <Link to="/transparence"
            className="inline-block px-12 py-5 bg-black text-white text-[11px] tracking-[0.3em] uppercase transition-all duration-500 hover:bg-gray-800">
            La Transparence
          </Link>
        </div>
      </section>
    </div>
  );
}
